import { ShieldCheck } from "lucide-react";

// ─────────────────────────────────────────────────────────────────
//  Our Story — Public marketing page (Desktop English only in this pass)
//  Asset-first hybrid workflow. Only two image assets used:
//    - /brand/jobclose-ai-approved-logo-mark.png (right brand panel)
//    - /brand/how-it-works/levi_headshot_approved.png (Levi sub-block)
//  All other UI is native React/CSS.
// ─────────────────────────────────────────────────────────────────

const COPY_STORY = {
  en: {
    kicker: "OUR STORY",
    h1: "Hi, I\u2019m Gabe Romo.",
    subhead_a: "Founder of JobClose AI.",
    subhead_b: "I\u2019m a contractor just like you.",
    // Founder story paragraphs. Emphasis lines are rendered as
    // standalone accent lines between paragraphs.
    p1: "I\u2019ve spent decades working in construction and real estate. I\u2019m a licensed GB-98 contractor based in Albuquerque, New Mexico, and I\u2019ve dealt with many of the same things contractors deal with every day\u2014estimating jobs, keeping projects organized, staying on top of customers, managing paperwork, watching costs, and trying to make sure nothing falls through the cracks.",
    p2: "Over the years, I spent thousands of dollars on different software and applications that promised to make running a business easier. Some did one thing well. Others were too complicated. And too often, I found myself paying for different systems that still didn\u2019t work together the way I actually worked.",
    accent_1: "I knew there had to be a better way.",
    p3: "So I built JobClose AI.",
    accent_2: "I didn\u2019t come from Silicon Valley. I came from the jobsite.",
    p4: "I\u2019m not a software giant trying to figure out how contractors work from a conference room. I\u2019m a contractor who understood the problem first\u2014and then worked with my engineering team to build the technology to solve it.",
    p5: "And don\u2019t mistake that for simple technology. Once you start working with JobClose AI and Levi, I think you\u2019ll see that the technology can hold its own with anybody. The difference is that it was built around the contractor instead of asking the contractor to change the way they work to fit the software.",
    p6: "I also wanted JobClose AI to stay affordable. I know what it\u2019s like to spend thousands of dollars on software just to run a contracting business. I wanted to build serious technology that a real contractor could actually afford and use\u2014not software that becomes more expensive every time you need another useful tool.",
    p7: "My team and I have put an enormous amount of time, thought, testing, and real-world experience into JobClose AI. We built it with the same kind of pride and attention I believe contractors bring to their own craft.",
    accent_3: "I built this for contractors because I understand contractors. I\u2019m one of you.",
    p8: "My hope is pretty simple: that JobClose AI helps you stay more organized, miss fewer opportunities, spend less time buried in paperwork, understand your numbers, and build a stronger business.",
    accent_4: "I hope you enjoy using JobClose AI as much as I\u2019ve enjoyed building it for you.",

    // Levi sub-block
    levi_heading: "And then there\u2019s Levi\u2026",
    levi_p1: "Levi is my real-life black Goldendoodle, not a fictional marketing character.",
    levi_p2: "He became the identity of the AI coworker at the center of JobClose AI.",
    levi_p3_a: "Inside JobClose AI, ",
    levi_p3_b: "Levi is your AI coworker.",
    levi_p4: "He helps contractors organize information, find what they need, understand their jobs, and keep work moving.",
    levi_p5: "He\u2019s not just an AI chatbot\u2014he\u2019s the conversational operating interface and coworker across JobClose AI.",

    // Right brand panel
    brand_wordmark_a: "JobClose",
    brand_wordmark_b: "AI",
    brand_tagline_a: "The AI Operating System",
    brand_tagline_b: "for Contractors",
    brand_closing_a: "Built for contractors,",
    brand_closing_b: "Because I\u2019m one of you.",
  },
};

// Spanish · natural first-person contractor voice.
// Uses "tú" for direct address (consistent with the rest of the
// public site) and "ustedes" only where Gabe addresses contractors
// as a collective — matches the warmth of "one of you." Facts and
// meaning preserved verbatim. Layout is unchanged; only strings.
COPY_STORY.es = {
  kicker: "NUESTRA HISTORIA",
  h1: "Hola, soy Gabe Romo.",
  subhead_a: "Fundador de JobClose AI.",
  subhead_b: "Soy contratista, igual que t\u00fa.",

  p1: "Llevo d\u00e9cadas trabajando en construcci\u00f3n y bienes ra\u00edces. Soy contratista con licencia GB-98 en Albuquerque, Nuevo M\u00e9xico, y he lidiado con muchas de las mismas cosas que los contratistas enfrentamos todos los d\u00edas: estimar trabajos, mantener los proyectos organizados, estar al tanto de los clientes, manejar el papeleo, cuidar los costos y tratar de que nada se escape.",
  p2: "Con los a\u00f1os, gast\u00e9 miles de d\u00f3lares en distintos programas y aplicaciones que promet\u00edan hacer m\u00e1s f\u00e1cil llevar un negocio. Algunos hac\u00edan bien una cosa. Otros eran demasiado complicados. Y muchas veces me encontr\u00e9 pagando por sistemas diferentes que, aun as\u00ed, no trabajaban juntos como yo trabajaba en realidad.",
  accent_1: "Sab\u00eda que ten\u00eda que haber una mejor manera.",
  p3: "As\u00ed que constru\u00ed JobClose AI.",
  accent_2: "No vengo de Silicon Valley. Vengo de la obra.",
  p4: "No soy un gigante del software tratando de descubrir c\u00f3mo trabajan los contratistas desde una sala de conferencias. Soy un contratista que entendi\u00f3 el problema primero\u2014y despu\u00e9s trabaj\u00f3 con mi equipo de ingenier\u00eda para construir la tecnolog\u00eda que lo resuelve.",
  p5: "Y no confundas eso con tecnolog\u00eda simple. Una vez que empieces a trabajar con JobClose AI y con Levi, creo que ver\u00e1s que la tecnolog\u00eda est\u00e1 a la altura de cualquiera. La diferencia es que fue construida en torno al contratista, en lugar de pedirle al contratista que cambie su forma de trabajar para acomodarse al software.",
  p6: "Tambi\u00e9n quer\u00eda que JobClose AI se mantuviera accesible. S\u00e9 lo que es gastar miles de d\u00f3lares en software solo para llevar un negocio de contratista. Quer\u00eda crear tecnolog\u00eda seria que un contratista real pudiera realmente costear y usar\u2014no un software que se vuelve m\u00e1s caro cada vez que necesitas otra herramienta \u00fatil.",
  p7: "Mi equipo y yo le hemos dedicado una enorme cantidad de tiempo, pensamiento, pruebas y experiencia del mundo real a JobClose AI. Lo construimos con el mismo orgullo y la misma atenci\u00f3n que creo que los contratistas le ponen a su propio oficio.",
  accent_3: "Constru\u00ed esto para los contratistas porque entiendo a los contratistas. Soy uno de ustedes.",
  p8: "Mi esperanza es sencilla: que JobClose AI te ayude a mantenerte m\u00e1s organizado, perder menos oportunidades, pasar menos tiempo enterrado en papeleo, entender tus n\u00fameros y construir un negocio m\u00e1s s\u00f3lido.",
  accent_4: "Espero que disfrutes usando JobClose AI tanto como yo he disfrutado constru\u00e9ndolo para ti.",

  levi_heading: "Y luego est\u00e1 Levi\u2026",
  levi_p1: "Levi es mi Goldendoodle negro de la vida real, no un personaje ficticio de marketing.",
  levi_p2: "Se convirti\u00f3 en la identidad del compa\u00f1ero de trabajo con IA en el centro de JobClose AI.",
  levi_p3_a: "Dentro de JobClose AI, ",
  levi_p3_b: "Levi es tu compa\u00f1ero de trabajo con IA.",
  levi_p4: "Ayuda a los contratistas a organizar la informaci\u00f3n, encontrar lo que necesitan, entender sus trabajos y mantener el trabajo en marcha.",
  levi_p5: "No es solo un chatbot de IA: es la interfaz operativa conversacional y tu compa\u00f1ero de trabajo en todo JobClose AI.",

  brand_wordmark_a: "JobClose",
  brand_wordmark_b: "AI",
  brand_tagline_a: "El sistema operativo con IA",
  brand_tagline_b: "para contratistas",
  brand_closing_a: "Hecho para contratistas.",
  brand_closing_b: "Porque soy uno de ustedes.",
};

const OurStoryBody = ({ lang = "en" }) => {
  const c = COPY_STORY[lang] || COPY_STORY.en;
  return (
    <main
      className="relative bg-white"
      data-testid="our-story-body"
    >
      {/* CHAPTER 1 — Founder story + JobClose AI brand panel.
          Deterministic parent-controlled two-column composition.
          Below lg, columns naturally stack to one — no duplicated
          mobile branch is built in this pass. */}
      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr]">
        {/* ─── LEFT: Founder story column ─────────────────────── */}
        <section
          className="bg-[#F5F7FB] px-6 py-14 sm:px-10 lg:px-20 lg:py-24"
          data-testid="our-story-left"
        >
          <div className="max-w-[720px]">
            <div className="inline-block px-3 py-1 rounded-full bg-[var(--jc-blue-soft)] text-[var(--jc-blue)] text-[11px] font-bold tracking-[0.14em]">
              {c.kicker}
            </div>
            <h1 className="mt-5 font-display font-extrabold tracking-tight text-[var(--jc-text)] text-[38px] lg:text-[52px] leading-[1.02]">
              {c.h1}
            </h1>
            <div className="mt-4 text-[var(--jc-blue)] font-display font-bold text-[20px] lg:text-[22px] leading-tight">
              <div>{c.subhead_a}</div>
              <div>{c.subhead_b}</div>
            </div>

            {/* Prose column · single vertical flow, natural growth.
                Emphasis lines are rendered inline between paragraphs
                as their own accent block. */}
            <div className="mt-9 space-y-6 text-[17px] lg:text-[19px] leading-[1.75] text-[var(--jc-text)] font-medium">
              <p>{c.p1}</p>
              <p>{c.p2}</p>
              <p className="text-[var(--jc-blue)] font-semibold text-[19px] lg:text-[21px]">
                {c.accent_1}
              </p>
              <p>{c.p3}</p>
              <p className="text-[var(--jc-text)] font-display font-extrabold text-[26px] lg:text-[30px] leading-[1.2] tracking-tight">
                {c.accent_2}
              </p>
              <p>{c.p4}</p>
              <p>{c.p5}</p>
              <p>{c.p6}</p>
              <p>{c.p7}</p>
              <p className="text-[var(--jc-blue)] font-semibold text-[19px] lg:text-[21px]">
                {c.accent_3}
              </p>
              <p>{c.p8}</p>
              <p className="text-[var(--jc-blue)] font-semibold text-[19px] lg:text-[21px]">
                {c.accent_4}
              </p>
            </div>
          </div>
        </section>

        {/* ─── RIGHT: JobClose navy branded panel (NOT sticky) ─── */}
        <aside
          className="relative bg-[var(--jc-navy)] text-white overflow-hidden px-8 py-16 lg:px-14 lg:py-20 flex flex-col items-center justify-center text-center"
          data-testid="our-story-right"
        >
          {/* Restrained blueprint-inspired background treatment —
              a subtle grid + soft blue radial bloom. Pure CSS,
              no extra assets. */}
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.10] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.9) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.9) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(60% 45% at 50% 45%, rgba(59,130,246,0.30) 0%, rgba(37,99,235,0.10) 45%, rgba(11,30,62,0) 78%)",
            }}
          />

          <div className="relative z-10 flex flex-col items-center max-w-[420px]">
            {/* Approved insignia */}
            <img
              src="/brand/jobclose-ai-approved-logo-mark.png"
              alt="JobClose AI"
              className="w-[200px] sm:w-[240px] lg:w-[300px] h-auto select-none"
              draggable={false}
              loading="eager"
              decoding="sync"
              data-testid="our-story-brand-mark"
            />

            {/* Wordmark */}
            <div className="mt-8 lg:mt-10 font-display font-extrabold tracking-tight text-[46px] sm:text-[54px] lg:text-[72px] leading-none">
              {c.brand_wordmark_a}{" "}
              <span className="text-[var(--jc-blue)]">{c.brand_wordmark_b}</span>
            </div>

            {/* Tagline */}
            <div className="mt-5 lg:mt-6 text-[18px] sm:text-[20px] lg:text-[22px] text-white/85 font-medium leading-[1.35]">
              <div>{c.brand_tagline_a}</div>
              <div>{c.brand_tagline_b}</div>
            </div>

            {/* Divider */}
            <div className="mt-8 lg:mt-10 w-24 h-px bg-white/25" />

            {/* Closing statement */}
            <div className="mt-8 lg:mt-10 font-display font-extrabold tracking-tight text-[26px] sm:text-[30px] lg:text-[38px] leading-[1.15]">
              <div>{c.brand_closing_a}</div>
              <div className="text-[var(--jc-blue)]">{c.brand_closing_b}</div>
            </div>
          </div>
        </aside>
      </div>

      {/* CHAPTER 2 — Levi. Full-width lower section beneath the
          two-column founder/brand composition. Same soft blue-gray
          background continues Gabe's story into Levi's chapter.
          Internal max-width centers the Levi + copy pair so it never
          stretches edge-to-edge on wide monitors. Levi image and
          copy are anchored as one stable parent flex row. */}
      <section
        className="bg-[#F5F7FB] px-6 py-16 sm:px-10 lg:px-20 lg:py-24"
        data-testid="our-story-chapter-2"
      >
        <div
          className="max-w-[960px] mx-auto flex flex-col items-center gap-6 lg:flex-row lg:items-start lg:gap-8"
          data-testid="our-story-levi"
        >
          <img
            src="/brand/how-it-works/levi_headshot_approved.png"
            alt="Levi"
            className="w-[220px] sm:w-[240px] lg:w-[280px] h-auto flex-shrink-0 select-none"
            draggable={false}
            loading="eager"
            decoding="sync"
            data-testid="our-story-levi-img"
          />
          <div>
            <h2 className="font-display font-extrabold text-[28px] sm:text-[30px] lg:text-[36px] text-[var(--jc-text)] leading-tight tracking-tight text-center lg:text-left">
              {c.levi_heading}
            </h2>
            <div className="mt-4 lg:mt-5 space-y-4 text-[16px] sm:text-[16.5px] lg:text-[18px] leading-[1.7] text-[var(--jc-text)] font-medium">
              <p>{c.levi_p1}</p>
              <p>{c.levi_p2}</p>
              <p>
                {c.levi_p3_a}
                <span className="text-[var(--jc-blue)] font-bold">
                  {c.levi_p3_b}
                </span>
              </p>
              <p>{c.levi_p4}</p>
              <p>{c.levi_p5}</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default OurStoryBody;
